/**
 * Keyboard Shortcuts Hook
 *
 * React hook for registering and handling keyboard shortcuts.
 * Provides a consistent way to bind shortcuts across the application.
 */

import { useEffect, useCallback, useRef } from 'react'
import type { ShortcutDefinition, ShortcutHandler } from './types'
import { matchesShortcut, SHORTCUTS } from './shortcuts'

/**
 * Options for useKeyboardShortcut hook
 */
interface UseKeyboardShortcutOptions {
  /** Whether the shortcut is enabled (default: true) */
  enabled?: boolean
  /** Element to attach listener to (default: document) */
  target?: HTMLElement | null
}

/**
 * Hook to register a single keyboard shortcut
 *
 * @example
 * ```tsx
 * // Using predefined shortcut
 * useKeyboardShortcut(SHORTCUTS.GLOBAL_SEARCH, () => {
 *   setSearchOpen(true)
 * })
 *
 * // With conditional enabling
 * useKeyboardShortcut(SHORTCUTS.SAVE, handleSave, {
 *   enabled: hasChanges,
 * })
 * ```
 */
export function useKeyboardShortcut(
  shortcut: ShortcutDefinition,
  handler: ShortcutHandler,
  options: UseKeyboardShortcutOptions = {}
): void {
  const { enabled = true, target } = options
  const handlerRef = useRef(handler)

  // Keep handler ref up to date
  useEffect(() => {
    handlerRef.current = handler
  }, [handler])

  useEffect(() => {
    if (!enabled || shortcut.enabled === false) return

    const element = target ?? document

    const handleKeyDown = (e: Event) => {
      const event = e as KeyboardEvent

      // Skip if user is typing in an input/textarea (unless shortcut explicitly allows it)
      if (isInputFocused() && !shortcut.modifiers.length) {
        return
      }

      if (matchesShortcut(event, shortcut)) {
        if (shortcut.preventDefault !== false) {
          event.preventDefault()
        }
        handlerRef.current(event)
      }
    }

    element.addEventListener('keydown', handleKeyDown)
    return () => element.removeEventListener('keydown', handleKeyDown)
  }, [shortcut, enabled, target])
}

/**
 * Hook to register multiple keyboard shortcuts at once
 *
 * @example
 * ```tsx
 * useKeyboardShortcuts({
 *   [SHORTCUTS.GLOBAL_SEARCH.id]: () => setSearchOpen(true),
 *   [SHORTCUTS.NEW_ITEM.id]: () => navigate('/new'),
 * })
 * ```
 */
export function useKeyboardShortcuts(
  handlers: Record<string, ShortcutHandler>,
  options: UseKeyboardShortcutOptions = {}
): void {
  const { enabled = true, target } = options
  const handlersRef = useRef(handlers)

  // Keep handlers ref up to date
  useEffect(() => {
    handlersRef.current = handlers
  }, [handlers])

  useEffect(() => {
    if (!enabled) return

    const element = target ?? document

    const handleKeyDown = (e: Event) => {
      const event = e as KeyboardEvent

      // Check each registered shortcut
      for (const [id, handler] of Object.entries(handlersRef.current)) {
        // Find the shortcut definition by id
        const found = Object.values(SHORTCUTS).find((s) => s.id === id)
        if (!found || ('enabled' in found && found.enabled === false)) continue

        // Cast to ShortcutDefinition for type safety
        const shortcut = found as ShortcutDefinition

        // Skip if user is typing in an input/textarea (unless shortcut has modifiers)
        if (isInputFocused() && !shortcut.modifiers.length) {
          continue
        }

        if (matchesShortcut(event, shortcut)) {
          if (shortcut.preventDefault !== false) {
            event.preventDefault()
          }
          handler(event)
          return // Only trigger first matching shortcut
        }
      }
    }

    element.addEventListener('keydown', handleKeyDown)
    return () => element.removeEventListener('keydown', handleKeyDown)
  }, [enabled, target])
}

/**
 * Hook for global shortcuts that should work across the entire app
 *
 * Use this in your root layout component to register app-wide shortcuts.
 *
 * @example
 * ```tsx
 * useGlobalShortcuts({
 *   onSearch: () => setSearchOpen(true),
 *   onHelp: () => setHelpOpen(true),
 * })
 * ```
 */
export function useGlobalShortcuts(callbacks: {
  onSearch?: () => void
  onHelp?: () => void
  onSave?: () => void
  onNew?: () => void
  onDelete?: () => void
  onDashboard?: () => void
  onBack?: () => void
  onForward?: () => void
}): void {
  const { onSearch, onHelp, onSave, onNew, onDelete, onDashboard, onBack, onForward } = callbacks

  useKeyboardShortcut(
    SHORTCUTS.GLOBAL_SEARCH,
    useCallback(() => onSearch?.(), [onSearch]),
    { enabled: !!onSearch }
  )

  useKeyboardShortcut(
    SHORTCUTS.SHOW_KEYBOARD_HELP,
    useCallback(() => onHelp?.(), [onHelp]),
    { enabled: !!onHelp }
  )

  useKeyboardShortcut(
    SHORTCUTS.SAVE,
    useCallback(() => onSave?.(), [onSave]),
    { enabled: !!onSave }
  )

  useKeyboardShortcut(
    SHORTCUTS.NEW_ITEM,
    useCallback(() => onNew?.(), [onNew]),
    { enabled: !!onNew }
  )

  useKeyboardShortcut(
    SHORTCUTS.DELETE_ITEM,
    useCallback(() => onDelete?.(), [onDelete]),
    { enabled: !!onDelete }
  )

  useKeyboardShortcut(
    SHORTCUTS.GO_TO_DASHBOARD,
    useCallback(() => onDashboard?.(), [onDashboard]),
    { enabled: !!onDashboard }
  )

  useKeyboardShortcut(
    SHORTCUTS.GO_BACK,
    useCallback(() => onBack?.(), [onBack]),
    { enabled: !!onBack }
  )

  useKeyboardShortcut(
    SHORTCUTS.GO_FORWARD,
    useCallback(() => onForward?.(), [onForward]),
    { enabled: !!onForward }
  )
}

/**
 * Hook for list navigation shortcuts (j/k to move, Enter to open)
 *
 * Use this in list pages to enable keyboard navigation.
 *
 * @example
 * ```tsx
 * const [selectedIndex, setSelectedIndex] = useState(-1)
 *
 * useListNavigation({
 *   itemCount: items.length,
 *   selectedIndex,
 *   onSelect: setSelectedIndex,
 *   onOpen: (index) => navigate(`/items/${items[index].id}`),
 *   enabled: !isInputFocused,
 * })
 * ```
 */
export function useListNavigation(options: {
  itemCount: number
  selectedIndex: number
  onSelect: (index: number) => void
  onOpen?: (index: number) => void
  enabled?: boolean
}): void {
  const { itemCount, selectedIndex, onSelect, onOpen, enabled = true } = options

  useKeyboardShortcut(
    SHORTCUTS.LIST_NEXT,
    useCallback(() => {
      if (itemCount === 0) return
      const nextIndex = selectedIndex < itemCount - 1 ? selectedIndex + 1 : 0
      onSelect(nextIndex)
    }, [itemCount, selectedIndex, onSelect]),
    { enabled }
  )

  useKeyboardShortcut(
    SHORTCUTS.LIST_PREV,
    useCallback(() => {
      if (itemCount === 0) return
      const prevIndex = selectedIndex > 0 ? selectedIndex - 1 : itemCount - 1
      onSelect(prevIndex)
    }, [itemCount, selectedIndex, onSelect]),
    { enabled }
  )

  useKeyboardShortcut(
    SHORTCUTS.LIST_OPEN,
    useCallback(() => {
      if (selectedIndex >= 0 && selectedIndex < itemCount && onOpen) {
        onOpen(selectedIndex)
      }
    }, [selectedIndex, itemCount, onOpen]),
    { enabled: enabled && !!onOpen && selectedIndex >= 0 }
  )
}

/**
 * Check if an input element is currently focused
 */
function isInputFocused(): boolean {
  const activeElement = document.activeElement
  if (!activeElement) return false

  const tagName = activeElement.tagName.toLowerCase()
  if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') {
    return true
  }

  // Check for contenteditable
  if (activeElement.getAttribute('contenteditable') === 'true') {
    return true
  }

  return false
}
